/* eslint-disable no-console */
// -- Disabling for what should be obvious reasons 😜

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  FATAL = 4,
  OFF = 5
}

/** Flags mirrored from `envClient` after init — used by {@link resolveClientLogLevel}. */
export interface ClientLogLevelEnvFlags {
  debugMode: boolean;
  isLocal: boolean;
  isProduction: boolean;
  isTestEnv: boolean;
}

/**
 * Default browser console verbosity for Nova clients: real production CDN builds use ERROR only; non-prod and test
 * deploys use INFO so operator traces (e.g. PAGS) appear without DEBUG_MODE; local or debugMode uses DEBUG.
 */
export function resolveClientLogLevel(flags: ClientLogLevelEnvFlags): LogLevel {
  if (flags.debugMode || flags.isLocal) { return LogLevel.DEBUG; }

  if (flags.isProduction && !flags.isTestEnv) { return LogLevel.ERROR; }
  return LogLevel.INFO;
}

/** Console-shaped emitter passed into `Logger.when*` callbacks (level check already applied). */
export type LoggerEmitFn = (message: unknown, ...optionalParams: unknown[]) => void;

/**
 * The Logger class provides logging functionality with different log levels.
 * This class should be used in place of console.log, console.error, etc.
 *
 * **Deferred logging (`whenDebug` / `whenInfo` / …):** use only when the callback does **meaningful work before**
 * calling `log` (date parsing, large string building, big object graphs). `Logger` already skips `console.*` when the
 * level would no-op, but **arguments to `Logger.info(...)` etc. are still evaluated** — `when*` runs the callback only
 * after the level check, so that work is skipped entirely. If you would only call `log(...)` with literals or existing
 * variables and no prior computation, use **`Logger.info` / `Logger.debug`** (etc.) instead; `when*` adds a closure for
 * no benefit there.
 */
export class Logger {
  public static logLevel: LogLevel = LogLevel.ERROR;

  public static log(message: unknown, ...optionalParams: unknown[]) {
    if (Logger.logLevel <= LogLevel.DEBUG) {
      // eslint-disable no-console
      console.log(message, ...optionalParams);
    }
  }

  public static error(message: unknown, ...optionalParams: unknown[]) {
    if (Logger.logLevel <= LogLevel.ERROR) {
      console.error(message, ...optionalParams);
    }
  }

  public static warn(message: unknown, ...optionalParams: unknown[]) {
    if (Logger.logLevel <= LogLevel.WARN) {
      console.warn(message, ...optionalParams);
    }
  }

  public static info(message: unknown, ...optionalParams: unknown[]) {
    if (Logger.logLevel <= LogLevel.INFO) {
      console.info(message, ...optionalParams);
    }
  }

  public static debug(message: unknown, ...optionalParams: unknown[]) {
    if (Logger.logLevel <= LogLevel.DEBUG) {
      console.debug(message, ...optionalParams);
    }
  }

  /**
   * Runs `fn` only if DEBUG would emit; passes `log` that writes at DEBUG without re-checking level.
   * @see Logger class comment — use only when deferring **non-trivial** work before `log(...)`.
   */
  public static whenDebug(fn: (log: LoggerEmitFn) => void): void {
    if (Logger.logLevel > LogLevel.DEBUG) { return; }
    fn((message, ...optionalParams) => {
      console.debug(message, ...optionalParams);
    });
  }

  /**
   * Runs `fn` only if INFO would emit; passes `log` that writes at INFO without re-checking level.
   * @see Logger class comment — use only when deferring **non-trivial** work before `log(...)`.
   */
  public static whenInfo(fn: (log: LoggerEmitFn) => void): void {
    if (Logger.logLevel > LogLevel.INFO) { return; }
    fn((message, ...optionalParams) => {
      console.info(message, ...optionalParams);
    });
  }

  /**
   * Runs `fn` only if WARN would emit; passes `log` that writes at WARN without re-checking level.
   * @see Logger class comment — use only when deferring **non-trivial** work before `log(...)`.
   */
  public static whenWarn(fn: (log: LoggerEmitFn) => void): void {
    if (Logger.logLevel > LogLevel.WARN) { return; }
    fn((message, ...optionalParams) => {
      console.warn(message, ...optionalParams);
    });
  }

  /**
   * Runs `fn` only if ERROR would emit; passes `log` that writes at ERROR without re-checking level.
   * @see Logger class comment — use only when deferring **non-trivial** work before `log(...)`.
   */
  public static whenError(fn: (log: LoggerEmitFn) => void): void {
    if (Logger.logLevel > LogLevel.ERROR) { return; }
    fn((message, ...optionalParams) => {
      console.error(message, ...optionalParams);
    });
  }

  public static trace(message: unknown, ...optionalParams: unknown[]) {
    if (Logger.logLevel <= LogLevel.DEBUG) {
      console.trace(message, ...optionalParams);
    }
  }

  public static fatal(message: unknown, ...optionalParams: unknown[]) {
    if (Logger.logLevel <= LogLevel.FATAL) {
      console.error(`FATAL: ${message}`, ...optionalParams);
    }
  }

  public static setLogLevel(level: LogLevel) {
    Logger.logLevel = level;

    // ! Testing: This ensures that the correct line number is still displayed in the js console
    // ! From: https://stackoverflow.com/questions/13815640/a-proper-wrapper-for-console-log-with-correct-line-number
    if (level === LogLevel.DEBUG) {
      this.log = console.log.bind(window.console);
    }
  }
}
